'use es6';

import { fromJS, Map as ImmutableMap, Set as ImmutableSet, List } from 'immutable'; // Helper/Utility functions
// Rather than doing recordTypeInstance.merge(new RecordType(payload)), we use this fxn.
// this is because null/undefined pre-defined values in the RecordType defaults will clobber any set values in the instance

export const mergeRecords = (RecordType, currentState, payload) => {
  if (currentState) {
    return new RecordType(currentState.toMap().merge(fromJS(payload)));
  } else {
    return new RecordType(fromJS(payload));
  }
}; // Similar to mergeRecords, but this variant won't overwrite any keys that already have values.
// Useful for merging records from disparate entity APIs without
// clobbering existing record values with nulls from a new record

export const reconcileRecords = (RecordType, currentState, payload) => {
  if (currentState) {
    return new RecordType(currentState.toMap().mergeWith((currentRecordValue, newRecordValue) => {
      const mergedValue = currentRecordValue === null ? newRecordValue : currentRecordValue;
      return mergedValue;
    }, payload));
  } else {
    return new RecordType(fromJS(payload));
  }
}; // Updates a reducer state with a payload of an array or a single entity
// properly forces all data into RecordType

export const getUpdatedReducerMap = (state, payload, RecordType, idKey = 'id') => {
  if (Array.isArray(payload) || payload instanceof ImmutableSet || payload instanceof List) {
    // Merge payload entries with any existing records
    // If all entries are records, opt for using reconcileRecords
    const mergeFunction = payload.every(entry => entry instanceof RecordType) ? reconcileRecords : mergeRecords;
    const entries = payload.toJS ? payload.toJS() : payload;
    const records = entries.map(obj => {
      return mergeFunction(RecordType, state.get(obj.id), obj);
    });
    const entityMap = records.reduce((map, obj) => {
      return map.set(obj[idKey], obj);
    }, ImmutableMap());
    return state.merge(entityMap);
  } // payload is single object w id


  const id = payload[idKey];
  const currentEntry = state.get(id, new RecordType({
    id
  }));
  return state.setIn([payload[idKey]], mergeRecords(RecordType, currentEntry, payload));
};
export const removeFromSetAtPath = (state, path, values) => {
  if (values instanceof List || values instanceof ImmutableSet || values instanceof Array) {
    return state.setIn(path, state.getIn(path).subtract(values));
  } else {
    return state.setIn(path, state.getIn(path).delete(values));
  }
};
export const addToSetAtPath = (state, path, values) => {
  if (values instanceof List || values instanceof ImmutableSet || values instanceof Array) {
    return state.setIn(path, state.getIn(path).union(values));
  } else {
    return state.setIn(path, state.getIn(path).add(values));
  }
};
export const updateInnerData = (state, key, data) => {
  return Object.assign({}, state, {
    _innerData: Object.assign({}, state._innerData, {
      [key]: data
    })
  });
};
export const resetInnerData = state => {
  return Object.assign({}, state, {
    _innerData: {}
  });
};
export const makeOptimisticUpdate = (state, id, updatedFields) => {
  const entityToUpdate = state.get(id);
  const updatedEntity = entityToUpdate.withMutations(entity => {
    entity.set('previousState', entityToUpdate).merge(updatedFields);
  });
  return state.set(id, updatedEntity);
};
export const rollbackOptimisticUpdate = (state, id) => {
  const entityToRollback = state.get(id).withMutations(entity => {
    entity.merge(entity.get('previousState')).set('previousState', null);
  });
  return state.set(id, entityToRollback);
};
export const finishOptimisticUpdate = (state, id) => {
  return state.setIn([id, 'previousState'], null);
}; // constructKey is a function that takes an object and returns a unique key
// dataMap map we're grabbing data from
// defaultData is what to return if there is no entry

export const getKeyedData = (constructKey, dataMap, defaultData = {}, opts = {}) => {
  return dataMap.get(constructKey(opts), defaultData);
};